import axios, { AxiosInstance } from "axios";
import { TokenStore } from "./tokenStore";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL ?? "http://localhost:4000/api";

/**
 * Cria um client axios que: injeta o access token em memória em toda requisição, e — num 401 —
 * tenta renovar a sessão exatamente uma vez via `refreshPath` (cookie httpOnly de refresh vai
 * junto por `withCredentials`) antes de repetir a chamada original. Usado para os dois domínios
 * de auth (staff e, na Fase 6, portal do cliente), cada um com seu próprio TokenStore e endpoint
 * de refresh — nunca compartilham token.
 */
export function createHttpClient(opts: { refreshPath: string; tokenStore: TokenStore }): AxiosInstance {
  const client = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
  });

  client.interceptors.request.use((config) => {
    const token = opts.tokenStore.get();
    if (token) {
      config.headers = config.headers ?? {};
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });

  let refreshing: Promise<string | null> | null = null;

  client.interceptors.response.use(
    (res) => res,
    async (error) => {
      const original = error.config;
      const status = error.response?.status;
      if (status !== 401 || original?._retried || original?.url === opts.refreshPath) {
        return Promise.reject(error);
      }
      original._retried = true;

      if (!refreshing) {
        refreshing = client
          .post(opts.refreshPath)
          .then((res) => {
            const token = res.data?.accessToken ?? null;
            opts.tokenStore.set(token);
            return token;
          })
          .catch(() => {
            opts.tokenStore.set(null);
            return null;
          })
          .finally(() => {
            refreshing = null;
          });
      }

      const newToken = await refreshing;
      if (!newToken) {
        return Promise.reject(error);
      }
      original.headers.Authorization = `Bearer ${newToken}`;
      return client(original);
    }
  );

  return client;
}
